import { PropertyValueMap, TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import type { Ref } from 'lit/directives/ref';
import { BlocksElement } from '../BlocksElement';
import '../tooltip/tooltip';
import styles from './tooltip-label-overflow.styles';

/**
 * @csspart tooltip-wrapper - The component's tooltip wrapper.
 * @csspart tooltip - The component's tooltip element.
 * @csspart part-label - The component's label.
 */
export class BlocksTooltipLabelOverflow extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Field's label
   */
  @property({ type: String })
  label = 'Label';

  /**
   * Delay for show tooltip when label is overflowing
   */
  @property({ type: Number })
  tooltipShowDelay = 0;

  /**
   * Delay for hide tooltip when label is overflowing
   */
  @property({ type: Number })
  tooltipHideDelay = 0;

  /**
   * Identifier of the input element
   */
  @property({ type: String })
  inputElId = '';

  /**
   * Trigger element resize
   */
  @property({ type: Boolean })
  elementResize = false;

  /**
   * Reference element to attach the events
   */
  @property({ type: Object })
  wrapperElementRef: Ref<HTMLElement> | undefined;

  /**
   * Show the tooltip when ellipsis appeared for label
   * This property will be used in datepicker, datetimepicker and daterangepicker components to enable and disable tooltip
   */
  @property({ type: Boolean })
  enableTooltip = false;

  /**
   * Display tooltip on hover, tab, click etc
   */
  @state()
  showTooltip = false;

  /**
   * Get the active cat input element
   */
  get activeCatInputElement() {
    return document.activeElement?.shadowRoot?.querySelector('cat-input-field');
  }

  /**
   * Get the input element
   */
  get inputElement(): HTMLInputElement | null | undefined {
    return this._elementRef?.querySelector(`[id='${this.inputElId}']`);
  }

  /**
   * Get the active input element
   */
  get activeInputElement(): HTMLElement | null | undefined {
    return (
      document.activeElement?.shadowRoot?.querySelector(`[id='${this.inputElId}']`) ||
      this.activeCatInputElement?.shadowRoot?.querySelector(`[id='${this.inputElId}']`)
    );
  }

  /**
   * Get the wrapper element
   */
  get _elementRef() {
    return this.wrapperElementRef?.value;
  }

  /**
   * Connected callback lifecycle
   */
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('mousedown', this.handleClick, false);
    if (this._elementRef) {
      this._elementRef.addEventListener('mouseover', this.activateTooltip);
      this._elementRef.addEventListener('mouseleave', this.deActivateTooltip);
      this._elementRef.addEventListener('focusin', this.handleFocus);
      this._elementRef.addEventListener('focusout', this.handleFocusout);
    }
  }

  /**
   * Disconnected callback lifecycle
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('mousedown', this.handleClick, false);
    if (this._elementRef) {
      this._elementRef.removeEventListener('mouseover', this.activateTooltip);
      this._elementRef.removeEventListener('mouseleave', this.deActivateTooltip);
      this._elementRef.removeEventListener('focusin', this.handleFocus);
      this._elementRef.removeEventListener('focusout', this.handleFocusout);
    }
  }

  /**
   * Updated lifecycle
   */
  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (
      (_changedProperties.has('label') && this.label != _changedProperties.get('label')) ||
      (_changedProperties.has('enableTooltip') && this.label != _changedProperties.get('enableTooltip') && this.enableTooltip)
    ) {
      this.setTooltip();
    }
  }

  /**
   * 1. Activate tooltip for lengthy label
   */
  private handleResize = () => {
    this.setTooltip();
  };

  /**
   * Enable the tooltip if ellipsis available for the label
   */
  private setTooltip = () => {
    const tooltipEl: HTMLElement | null | undefined = this._elementRef?.querySelector("span[part*='part-label']");
    if (tooltipEl) {
      this.enableTooltip = tooltipEl.offsetWidth < tooltipEl.scrollWidth;
    }
  };

  /**
   * Show the tooltip in case of mouseover, tab, click etc
   */
  private activateTooltip = () => {
    this.showTooltip = true;
  };

  /**
   * hide the tooltip in case of mouseout, tab focus out etc
   */
  private deActivateTooltip = () => {
    if (this.activeInputElement !== this.inputElement) {
      this.showTooltip = false;
    }
  };

  /**
   * Handle focus event
   */
  private handleFocus = () => {
    this.activateTooltip();
  };

  /**
   * Handle focus out event
   */
  private handleFocusout = () => {
    this.showTooltip = false;
  };

  /**
   * Handle click event
   */
  private handleClick = (e: Event) => {
    if (this._elementRef && !e.composedPath().includes(this._elementRef)) {
      this.showTooltip = false;
    }
  };

  render(): TemplateResult<1> {
    return html` ${this.enableTooltip
      ? html`<div class="cat-c-input-field__tooltip-wrapper" part="tooltip-wrapper">
          <cat-tooltip
            aria-hidden=${true}
            part="tooltip"
            exportparts=${this.getExportParts('tooltip')}
            ?isActive=${this.showTooltip}
            position=${'top-start'}
            ?isFlipAllowed=${true}
            class="cat-c-input-field__tooltip"
            ?isInline=${true}
            showDelay=${ifDefined(this.tooltipShowDelay)}
            hideDelay=${ifDefined(this.tooltipHideDelay)}
            ?elementResize=${this.elementResize}
          >
            <span part="part-label" class="cat-c-input-field__label">${this.label}</span>
          </cat-tooltip>
        </div>`
      : nothing}`;
  }
}

if (customElements.get('cat-tooltip-label-overflow') === undefined) {
  customElements.define('cat-tooltip-label-overflow', BlocksTooltipLabelOverflow);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-tooltip-label-overflow': BlocksTooltipLabelOverflow;
  }
}
